import type { NewSegment } from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { addSegments, loadHostQueue, loadStreams, openStream } from "../../core/ops/live-ops";
import { errorText } from "../../core/primitives/errors";
import { failedForm, failedLoad, idleForm, loaded, loading } from "../../core/primitives/load-status";
import { LineupPickerController } from "../../sections/lineup-picker/lineup-picker-controller";
import { groupStreams, LiveStreamsActions, LiveStreamsState } from "./live-streams-model";

export type LiveStreamsDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "locationsRepository" | "shareLinks"
>;

export interface LiveStreamsRouting {
  toStream(streamId: string): void;
}

export const STREAMS_POLL_MS = 30_000;
export const QUEUE_POLL_MS = 10_000;

/**
 * The queue and the streams. Both poll: the queue because every sale moves a
 * break, and there is no room to subscribe to before a stream exists.
 */
export class LiveStreamsController extends SubtreeController implements LiveStreamsActions {
  readonly state = new LiveStreamsState();
  readonly picker: LineupPickerController;
  #shopId: string | null = null;
  #poll: ReturnType<typeof setInterval> | null = null;
  #queuePoll: ReturnType<typeof setInterval> | null = null;

  constructor(
    readonly deps: LiveStreamsDeps,
    readonly routing: LiveStreamsRouting,
  ) {
    super();
    this.subtree.put(LiveStreamsState, this.state);
    this.subtree.put(LiveStreamsActions, this);
    this.sync(() => {
      const shown = this.state.queue.value.filter((b) => b.status !== "DRAFT" && b.status !== "CANCELLED");
      this.state.breakShares.value = new Map(shown.map((b) => [b.id, deps.shareLinks.break(b)]));
    }, [this.state.queue]);
    this.picker = this.own(
      new LineupPickerController(this.subtree, deps, { commit: (items, locationId) => this.#open(items, locationId) }),
    );
    this.sync(() => {
      const groups = groupStreams(this.state.streams.value);
      this.state.groups.value = groups;
      this.state.open.value = [...groups.live, ...groups.upcoming];
    }, [this.state.streams]);
    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      if (shopId === this.#shopId) return;
      this.#shopId = shopId;
      this.#restart(shopId);
    }, [deps.storeRepository]);
    this.autoDispose(() => this.#stopPolling());
  }

  reload = async (): Promise<void> => {
    const shopId = this.#shopId;
    if (!shopId) return;
    await Promise.all([this.#load(shopId), this.#loadQueue(shopId)]);
  };

  goLive = () => {
    if (!this.#shopId) return;
    this.state.goLive.value = idleForm;
    const locations = this.deps.locationsRepository.locations.filter((l) => l.isActive);
    if (locations.length === 0) {
      this.state.goLive.value = failedForm(
        "A stream broadcasts from one of your shop's locations. Add your shop's address in Settings first.",
      );
      return;
    }
    // One stream per location: an open stream at this counter is the one to go to.
    const hereId = this.deps.storeRepository.locationId ?? (locations.length === 1 ? locations[0].id : null);
    const openHere = hereId && this.state.open.value.find((s) => s.locationId === hereId);
    if (openHere) {
      this.routing.toStream(openHere.id);
      return;
    }
    this.picker.show("go-live");
  };

  /** Opens the stream (or finds the one open there) and adds what was picked. */
  async #open(items: NewSegment[], locationId: string | null): Promise<string | null> {
    const shopId = this.#shopId;
    if (!shopId) return null;
    if (!locationId) return "Choose where you are streaming from.";
    const store = this.deps.storeRepository.selectedStore;
    const title = `${store?.name ?? "Live"} · ${new Date().toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
    const opened = await openStream({ ...this.deps, shopId, locationId, title });
    if (!opened.ok) return errorText(opened.errors);
    const { streamId } = opened.value;
    if (items.length > 0) {
      const added = await addSegments({ ...this.deps, streamId, items });
      if (!added.ok) return errorText(added.errors);
    }
    if (!this.isDisposed) this.routing.toStream(streamId);
    return null;
  }

  #restart(shopId: string | null): void {
    this.#stopPolling();
    if (!shopId) return;
    this.state.status.value = loading;
    this.state.queueStatus.value = loading;
    void this.#load(shopId);
    void this.#loadQueue(shopId);
    this.#poll = setInterval(() => void this.#load(shopId), STREAMS_POLL_MS);
    this.#queuePoll = setInterval(() => void this.#loadQueue(shopId), QUEUE_POLL_MS);
  }

  #stopPolling(): void {
    if (this.#poll) clearInterval(this.#poll);
    if (this.#queuePoll) clearInterval(this.#queuePoll);
    this.#poll = null;
    this.#queuePoll = null;
  }

  async #load(shopId: string): Promise<void> {
    const r = await loadStreams({ ...this.deps, shopId, locationId: this.deps.storeRepository.locationId });
    if (this.isDisposed || this.#shopId !== shopId) return;
    if (!r.ok) {
      this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.state.streams.value = r.value;
    this.state.status.value = loaded;
  }

  async #loadQueue(shopId: string): Promise<void> {
    const r = await loadHostQueue({ ...this.deps, shopId });
    if (this.isDisposed || this.#shopId !== shopId) return;
    if (!r.ok) {
      this.state.queueStatus.value = failedLoad(r.errors);
      return;
    }
    this.state.queue.value = r.value;
    this.state.queueStatus.value = loaded;
  }
}
